/**
 * Analytics Page - GA4 Analytics Dashboard
 * 
 * Migrated from seo-engine-webtools-customer-module
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Skeleton,
  ToggleButtonGroup,
  ToggleButton,
  Fade,
  Button,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAnalyticsData } from '@/hooks/customer';
import { getLastNDays, formatCompactNumber, formatPercentage, formatDuration } from '@/services/customer/formatters';
import { MetricCard, TrendChart, SourcesChart, DataTable, InlineError, MetricCardSkeleton, ChartSkeleton, TableSkeleton } from '@/components/customer';
import type { Column } from '@/components/customer';

function CustomerAnalyticsPage() {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState(28);
  
  const { startDate, endDate } = getLastNDays(dateRange);

  const { data, isLoading, error, refetch } = useAnalyticsData({
    startDate,
    endDate,
  });

  const handleDateRangeChange = (_event: React.MouseEvent<HTMLElement>, newRange: number | null) => {
    if (newRange !== null) {
      setDateRange(newRange);
    }
  };

  // Show skeleton while loading
  if (isLoading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Skeleton variant="text" width={150} height={40} />
            <Skeleton variant="text" width={150} height={24} />
          </Box>
          <Skeleton variant="rectangular" width={350} height={32} />
        </Box>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={6} md={3} key={i}>
              <MetricCardSkeleton />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <ChartSkeleton height={300} variant="line" />
          </Grid>
          <Grid item xs={12} md={4}>
            <ChartSkeleton height={300} variant="pie" />
          </Grid>
        </Grid>
        <TableSkeleton rows={10} columns={5} />
      </Box>
    );
  }

  // Transform top pages for table display - ensure topPages array exists
  const topPages = data?.topPages ?? [];
  const tableData = topPages.map((page, index) => ({
    id: index,
    pagePath: page.pagePath,
    pageTitle: page.pageTitle || '-',
    pageViews: page.pageViews,
    uniquePageViews: page.uniquePageViews,
    avgTimeOnPage: page.avgTimeOnPage,
  }));

  const columns: Column<typeof tableData[0]>[] = [
    { field: 'pagePath', headerName: 'Page Path', flex: 2 },
    { field: 'pageTitle', headerName: 'Page Title', flex: 2 },
    { field: 'pageViews', headerName: 'Page Views', width: 120, align: 'right', valueGetter: (row) => formatCompactNumber(row.pageViews) },
    { field: 'uniquePageViews', headerName: 'Unique Views', width: 120, align: 'right', valueGetter: (row) => formatCompactNumber(row.uniquePageViews) },
    { field: 'avgTimeOnPage', headerName: 'Avg Time', width: 100, align: 'right', valueGetter: (row) => formatDuration(row.avgTimeOnPage) },
  ];

  return (
    <Fade in timeout={300}>
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/customer')}
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {startDate} to {endDate}
            </Typography>
          </Box>

          <ToggleButtonGroup
            value={dateRange}
            exclusive
            onChange={handleDateRangeChange}
            size="small"
          >
            <ToggleButton value={7}>7 Days</ToggleButton>
            <ToggleButton value={14}>14 Days</ToggleButton>
            <ToggleButton value={28}>28 Days</ToggleButton>
            <ToggleButton value={90}>90 Days</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Show error alert if data failed to load */}
        {error && (
          <InlineError error={error} title="Failed to load analytics data" onRetry={refetch} />
        )}

        {/* Metric Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <MetricCard
              title="Total Sessions"
              value={formatCompactNumber(data?.summary?.totalSessions ?? 0)}
              tooltip="Total number of sessions initiated by users."
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard
              title="Total Users"
              value={formatCompactNumber(data?.summary?.totalUsers ?? 0)}
              tooltip="Total number of unique users who visited your site."
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard
              title="Engagement Rate"
              value={formatPercentage(data?.summary?.engagementRate ?? 0)}
              tooltip="Percentage of sessions that were engaged."
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard
              title="Avg Session Duration"
              value={formatDuration(data?.summary?.avgSessionDuration ?? 0)}
              tooltip="Average time users spend on your site per session."
            />
          </Grid>
        </Grid>

        {/* Charts Row */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Daily Trend
                </Typography>
                <TrendChart data={data?.dailyTrend ?? []} />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Traffic Sources
                </Typography>
                <SourcesChart data={data?.topSources ?? []} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Top Pages Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Top Pages
            </Typography>
            <DataTable data={tableData} columns={columns} pageSize={10} />
          </CardContent>
        </Card>
      </Box>
    </Fade>
  );
}

export default CustomerAnalyticsPage;
